import { useEffect, useMemo } from 'react';
import { generatePath, useParams } from 'react-router-dom';
import { useUserProfile } from 'src/context/userProfileContext';
import { paths } from 'src/router/paths';
import { useTrackEvents } from 'src/context/userProfileContext/hooks';
import { ComponentSource, ContentType, TryResult } from 'src/utils/datahubDefaultEvent';
import { Card } from '../card';
import { InterestsSlider } from '../interestsSlider';
import { useMyCommunities } from '../../hooks';
import { CardSkeleton } from '../cardSkeleton';
import { CommunitiesErrorState } from '../communitiesErrorState';
import { CommunitiesProps, PathParams } from './communities.types';
import { Wrapper } from './communities.styles';
import { MAX_COMMUNITIES_LENGTH } from './communities.constants';

const Communities = ({ className, active, onSuccess }: CommunitiesProps) => {
  const { lang } = useParams() as PathParams;
  const { visible, loggedUserUcode } = useUserProfile();
  const { sendReadActivitiesEvent } = useTrackEvents({
    loggedUserUcode
  });

  const {
    isLoading,
    isError,
    isSuccess,
    error,
    data: communities
  } = useMyCommunities({ enabled: visible });

  const isValidCommunities = useMemo(() => {
    return Array.isArray(communities) && communities?.length > 0;
  }, [communities]);

  const communitiesList = useMemo(() => {
    return isValidCommunities ? communities?.slice(0, MAX_COMMUNITIES_LENGTH) : [];
  }, [isValidCommunities, communities]);

  const generatePathname = ({ membershipSlug, id }) => {
    return generatePath(paths.COMMUNITY_PATH, {
      lang,
      membership: membershipSlug,
      idCommunity: id
    });
  };

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.(communitiesList?.length);
    }
  }, [isSuccess, communitiesList]);

  useEffect(() => {
    if (active && isSuccess && isValidCommunities) {
      sendReadActivitiesEvent({
        contentType: ContentType.COMMUNITY,
        itemsCount: Number(communities?.length),
        componentSource: ComponentSource.COMMUNITY_LIST,
        result: TryResult.SUCCESSFUL,
        statusCode: 200
      });
    }

    if (active && isError) {
      sendReadActivitiesEvent({
        contentType: ContentType.COMMUNITY,
        itemsCount: 0,
        componentSource: ComponentSource.COMMUNITY_LIST,
        result: TryResult.FAILURE,
        statusCode: error?.statusCode,
        failureDescription: error?.message
      });
    }
  }, [active, isSuccess, isError]);

  if (isLoading) {
    return (
      <Wrapper className={className}>
        <CardSkeleton />
      </Wrapper>
    );
  }

  if (isError || !isValidCommunities) {
    return (
      <Wrapper className={className}>
        <CommunitiesErrorState />
      </Wrapper>
    );
  }

  return (
    <Wrapper className={className}>
      <InterestsSlider>
        {communitiesList?.map((community) => (
          <Card
            key={community.id}
            href={generatePathname(community)}
            title={community.name}
            text={community.membershipName}
          />
        ))}
      </InterestsSlider>
    </Wrapper>
  );
};

export default Communities;
